import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCards, createCard, updateCard, deleteCard, seedDatabase, getMe } from '@/lib/api';
import { toast } from 'sonner';

const CARD_TYPES = ['Cashback', 'Travel', 'Rewards', 'Premium', 'Islamic'];
const BANKS = ['Emirates NBD', 'ADCB', 'First Abu Dhabi Bank', 'Mashreq', 'Dubai Islamic Bank', 'RAK Bank'];
const BANK_COLORS = {
  'Emirates NBD': '#C41230', 'ADCB': '#A51C30', 'First Abu Dhabi Bank': '#B5985A',
  'Mashreq': '#F26522', 'Dubai Islamic Bank': '#006A4E', 'RAK Bank': '#003399',
};

const emptyForm = {
  bank_name: '', card_name: '', card_type: 'Cashback', annual_fee: 0,
  interest_rate_monthly: 0, cashback_rate: 0, rewards_description: '', min_salary: 0,
  welcome_bonus: '', features: '', benefits: '', bank_color: '#002FA7',
  is_islamic: false, foreign_transaction_fee: 0, lounge_access: '', insurance_coverage: '',
  contactless: true, supplementary_cards: 0,
};

export default function AdminPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { navigate('/admin/login'); return; }
    getMe().then(r => { setAuthed(r.data); fetchCards(); })
      .catch(() => { localStorage.removeItem('adminToken'); navigate('/admin/login'); });
  }, []);

  const fetchCards = () => {
    setLoading(true);
    getCards({}).then(r => setCards(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const handleChange = (key, value) => {
    setForm(prev => ({
      ...prev, [key]: value,
      ...(key === 'bank_name' && BANK_COLORS[value] ? { bank_color: BANK_COLORS[value] } : {}),
    }));
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (card) => {
    setEditingId(card.id);
    setForm({
      ...card,
      features: Array.isArray(card.features) ? card.features.join(', ') : card.features,
      benefits: Array.isArray(card.benefits) ? card.benefits.join(', ') : card.benefits,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.bank_name || !form.card_name) { toast.error('Bank and card name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        annual_fee: parseFloat(form.annual_fee) || 0,
        interest_rate_monthly: parseFloat(form.interest_rate_monthly) || 0,
        cashback_rate: parseFloat(form.cashback_rate) || 0,
        min_salary: parseFloat(form.min_salary) || 0,
        foreign_transaction_fee: parseFloat(form.foreign_transaction_fee) || 0,
        supplementary_cards: parseInt(form.supplementary_cards) || 0,
        features: typeof form.features === 'string' ? form.features.split(',').map(s => s.trim()).filter(Boolean) : form.features,
        benefits: typeof form.benefits === 'string' ? form.benefits.split(',').map(s => s.trim()).filter(Boolean) : form.benefits,
      };
      if (editingId) { await updateCard(editingId, payload); toast.success('Card updated'); }
      else { await createCard(payload); toast.success('Card created'); }
      setDialogOpen(false);
      fetchCards();
    } catch (err) {
      if (err.response?.status === 401) { toast.error('Session expired'); handleLogout(); }
      else toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this card?')) return;
    try { await deleteCard(id); toast.success('Deleted'); fetchCards(); }
    catch (err) {
      if (err.response?.status === 401) { toast.error('Session expired'); handleLogout(); }
      else toast.error('Delete failed');
    }
  };

  const handleSeed = async () => {
    try { const res = await seedDatabase(); toast.success(res.data.message); fetchCards(); }
    catch (err) {
      if (err.response?.status === 401) { toast.error('Session expired'); handleLogout(); }
      else toast.error('Seed failed');
    }
  };

  if (authed === null) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#002FA7] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-xl text-[#09090B]">Admin Panel</h1>
            <p className="text-[12px] text-[#71717A]">{authed?.email} &middot; {cards.length} cards</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="seed-btn" onClick={handleSeed} className="text-[12px] rounded-full">
            Seed Data
          </Button>
          <Button size="sm" data-testid="add-card-btn" onClick={openCreate} className="bg-[#002FA7] hover:bg-[#002175] text-white gap-1.5 text-[12px] rounded-full">
            <Plus className="w-3.5 h-3.5" /> Add Card
          </Button>
          <Button variant="ghost" size="sm" data-testid="admin-logout-btn" onClick={handleLogout} className="text-[12px] text-[#71717A] rounded-full gap-1">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#E4E4E7] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
                {['Bank', 'Card Name', 'Type', 'Annual Fee', 'Cashback', 'Min Salary', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#002FA7]" /></td></tr>
              ) : cards.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-[#71717A] text-sm">No cards. Click "Seed Data" to populate.</td></tr>
              ) : cards.map(card => (
                <tr key={card.id} className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA] transition-colors" data-testid={`admin-row-${card.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: card.bank_color }} />
                      <span className="text-[13px] font-medium">{card.bank_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[13px] font-medium text-[#09090B]">{card.card_name}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-[10px] rounded-full">{card.card_type}</Badge></td>
                  <td className="px-4 py-3 number-display text-[13px]">{card.annual_fee === 0 ? <span className="text-[#059669] font-medium">FREE</span> : `AED ${card.annual_fee}`}</td>
                  <td className="px-4 py-3 number-display text-[13px]">{card.cashback_rate > 0 ? `${card.cashback_rate}%` : '\u2014'}</td>
                  <td className="px-4 py-3 number-display text-[13px]">AED {card.min_salary.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" data-testid={`edit-btn-${card.id}`} onClick={() => openEdit(card)} className="h-7 w-7 p-0 rounded-full hover:bg-[#EEF2FF]">
                        <Pencil className="w-3.5 h-3.5 text-[#002FA7]" />
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`delete-btn-${card.id}`} onClick={() => handleDelete(card.id)} className="h-7 w-7 p-0 rounded-full hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="card-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingId ? 'Edit Card' : 'Add New Card'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Bank Name</Label>
              <Select value={form.bank_name} onValueChange={v => handleChange('bank_name', v)}>
                <SelectTrigger data-testid="form-bank-name" className="mt-1"><SelectValue placeholder="Select bank" /></SelectTrigger>
                <SelectContent>{BANKS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Card Name</Label>
              <Input data-testid="form-card-name" value={form.card_name} onChange={e => handleChange('card_name', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Card Type</Label>
              <Select value={form.card_type} onValueChange={v => handleChange('card_type', v)}>
                <SelectTrigger data-testid="form-card-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CARD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Annual Fee (AED)</Label>
              <Input data-testid="form-annual-fee" type="number" value={form.annual_fee} onChange={e => handleChange('annual_fee', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Interest Rate (%/mo)</Label>
              <Input data-testid="form-interest-rate" type="number" step="0.01" value={form.interest_rate_monthly} onChange={e => handleChange('interest_rate_monthly', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Cashback Rate (%)</Label>
              <Input data-testid="form-cashback-rate" type="number" step="0.1" value={form.cashback_rate} onChange={e => handleChange('cashback_rate', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Min Salary (AED)</Label>
              <Input data-testid="form-min-salary" type="number" value={form.min_salary} onChange={e => handleChange('min_salary', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Supplementary Cards</Label>
              <Input data-testid="form-supp-cards" type="number" value={form.supplementary_cards} onChange={e => handleChange('supplementary_cards', e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Welcome Bonus</Label>
              <Input data-testid="form-welcome-bonus" value={form.welcome_bonus} onChange={e => handleChange('welcome_bonus', e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Rewards Description</Label>
              <Input data-testid="form-rewards" value={form.rewards_description} onChange={e => handleChange('rewards_description', e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Features (comma-separated)</Label>
              <Input data-testid="form-features" value={form.features} onChange={e => handleChange('features', e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Benefits (comma-separated)</Label>
              <Input data-testid="form-benefits" value={form.benefits} onChange={e => handleChange('benefits', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Lounge Access</Label>
              <Input data-testid="form-lounge" value={form.lounge_access} onChange={e => handleChange('lounge_access', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Insurance Coverage</Label>
              <Input data-testid="form-insurance" value={form.insurance_coverage} onChange={e => handleChange('insurance_coverage', e.target.value)} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-card-btn" onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002175] text-white gap-1.5 rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editingId ? 'Update Card' : 'Create Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
